/**
 * The ONLY place names live. Trademark clearance is pending for the game name;
 * character names are locked per Systems Brief v1 §10.4 (Grandpa, Biscuit). Rename here and nowhere else.
 */
export const GAME_NAME = 'Sackfall';
export const GAME_VERSION = '0.6.0';
/** Hosted tester build (Cloudflare Pages, deploys `main`). The single-file artifact is a fallback only. */
export const TESTER_URL = 'https://sackfall.pages.dev';
/** The one shaft that exists until WP7 (label on the title CTA and the shaft sign). */
export const SHAFT_LABEL = 'Shaft 1';
/** Grandpa hint lines (≤8 words, MVP v0.2 §2). Shown once per profile via progress.seen. */
export const HINTS = {
  fullLantern: 'Full lantern. Fire it.',
} as const;

export const STRINGS = {
  miner: 'Pip',
  grandpa: 'Grandpa',
  dog: 'Biscuit',
  crawler: 'Crawler',
  borer: 'Borer',
  theme: 'Sunny Seam',
} as const;

/**
 * Asset manifest names (Bible §5). The atlas contract: real art exported under these exact names
 * drops in with no code change. Placeholder shapes are baked under the same names for now.
 */
export const ASSETS = {
  /** Variant A lantern: 5 fill levels (0 = empty, 4 = full) + a flash frame. */
  lanternFill: (level: number) => `lantern.fill.${level}`,
  lanternFlash: 'lantern.flash',
  lanternLevels: 5,
  /** Variant B flame icon beside Pip: 5 fill levels. */
  flameIconFill: (level: number) => `flameIcon.fill.${level}`,
  flameIconLevels: 5,
  /** Grandpa bust states (7 stills + snore loop of 4). */
  grandpa: {
    neutral: 'grandpa.neutral',
    browsUp: 'grandpa.browsUp',
    wince: 'grandpa.wince',
    snore: (frame: number) => `grandpa.snore.${frame}`,
    snoreFrames: 4,
    partyHat: 'grandpa.partyHat',
    delighted: 'grandpa.delighted',
    gobsmacked: 'grandpa.gobsmacked',
    content: 'grandpa.content',
    worried: 'grandpa.worried',
  },
  /** Headframe restoration states. */
  headframe: { collapsed: 'headframe.collapsed', patched: 'headframe.patched', restored: 'headframe.restored' },
  sign: 'ui.sign',
  brassPlate: 'ui.brassPlate',
  helmetLife: 'icon.helmet',
  cloud: (n: number) => `sky.cloud.${n}`,
  cloudCount: 3,
  floor: { rails: 'floor.rails', cart: 'floor.cart', lanternPost: 'floor.lanternPost', rope: 'floor.rope', logs: 'floor.logs' },
  /** Earth material: one continuous sheet (4×4 cells) tiled under the field, plus 16 cut variants. */
  earthMaterial: 'tile.earth.full',
  earthTile: (n: number) => `tile.earth.${n}`,
  earthTiles: 16,
  tunnel: { fill: 'tile.tunnel', rim: 'tile.tunnel.rim', end: 'tile.tunnel.end', lit: 'tile.tunnel.lit' },
  /** Headframe crops for the HUD: the sign band and the structure above it, per restoration state. */
  headframeSign: (state: 'collapsed' | 'patched' | 'restored') => `headframe.sign.${state}`,
  headframeTop: (state: 'collapsed' | 'patched' | 'restored') => `headframe.top.${state}`,
  /** Pip poses (static keys in WP4b milestone 1; sequences in milestone 3). */
  pip: { idle: 'pip.idle.0', dig: ['pip.dig.0', 'pip.dig.1'], walk: 'pip.walk.0', skid: 'pip.skid.0', fire: 'pip.fire.0', crushed: 'pip.crushed.0', bonus: 'pip.bonus.0', clear: 'pip.clear.0' },
  crawlerState: { waddle: ['crawler.waddle.0', 'crawler.waddle.1', 'crawler.waddle.2'], flatten: ['crawler.flatten.0', 'crawler.flatten.1'], flee: 'crawler.flee.0', flamed: ['crawler.flamed.0', 'crawler.flamed.1'], stuck: 'crawler.stuck.0' },
  borerState: { dig: ['borer.dig.0', 'borer.dig.1', 'borer.dig.2', 'borer.dig.3', 'borer.dig.4'], eat: 'borer.eat.0', flatten: 'borer.flatten.0', flamed: 'borer.flamed.0', revert: 'borer.revert.0' },
  mutation: (stage: number) => `creature.mutate.${stage}`,
  mutationStages: 5,
  sackState: { rest: 'sack.rest', wobble: ['sack.wobble.0', 'sack.wobble.1'], fall: 'sack.fall', land: 'sack.land.0', leanL: 'sack.lean.l', leanR: 'sack.lean.r', burst: 'sack.break.0', closed: 'sack.closed' },
  /** Full-bleed painted scenes (Bible §4): title/surface and the shaft level select. 4×6 cells. */
  scene: { surface: 'scene.surface', shaft: 'scene.shaft' },
  /** Wall lamps for the shaft: off (locked), lit (cleared), next (glowing, with a gem). */
  lamp: { off: 'lamp.off', lit: 'lamp.lit', next: 'lamp.next' },
  dangerTag: 'ui.dangerTag',
  /** Objectives (WP5): obstacle sprites (ART4) and the Target panel with its six goal icons (ART5). */
  obstacle: {
    pillar: ['obstacle.pillar.0', 'obstacle.pillar.1', 'obstacle.pillar.2'],
    rubble: 'obstacle.rubble',
    boulder: 'obstacle.boulder',
    hardDirt: ['obstacle.hardDirt.0', 'obstacle.hardDirt.1'],
    lockedGem: 'obstacle.lockedGem',
    switch: 'obstacle.switch',
    springActive: 'obstacle.spring.active',
    springDormant: 'obstacle.spring.dormant',
    nestClosed: 'obstacle.nest.closed',
    nestOpen: 'obstacle.nest.open',
  },
  /** WP6 kit and lantern (Systems Brief §2): meter 0–5, charge lantern, beam parts, flame frames, shield states, turbo frames, kit icons. */
  lanternMeter: (level: number) => `lantern.meter.${level}`,
  lanternMeterLevels: 6,
  lanternCharge: 'lantern.charge',
  beam: { head: 'beam.head', body: 'beam.body', tail: 'beam.tail' },
  flameFrames: ['flame.0', 'flame.1', 'flame.2', 'flame.3', 'flame.4'],
  shield: ['shield.0', 'shield.1', 'shield.2'],
  turbo: { burst: 'turbo.burst', frames: ['turbo.0', 'turbo.1', 'turbo.2'] },
  kit: { shield: 'ui.kit.shield', turbo: 'ui.kit.turbo', beam: 'ui.kit.beam', fruit: 'ui.kit.fruit', medkit: 'ui.kit.medkit', lock: 'ui.kit.lock' },
  kitFruit: 'kit.fruit',
  /** ART6 sheets registered but unsliced until WP9/WP11 (placeholders resolve them; see tests/assets.test.ts). */
  raw: {
    hubStage1: 'scene.hub.stage1', hubStage3: 'scene.hub.stage3', hubStage5: 'scene.hub.stage5',
    museumGems: 'sheet.museumGems', museumInterior: 'scene.museumInterior', minigameSigns: 'sheet.minigameSigns', shareCard: 'ui.shareCard',
  },
  targetPanel: 'ui.targetPanel',
  goalIcon: (goal: 'collect' | 'crush' | 'break' | 'survive' | 'clear' | 'rescue') => `ui.goalIcon.${goal}`,
  goalTypes: ['collect', 'crush', 'break', 'survive', 'clear', 'rescue'] as const,
  /** Bonus timer ring around Grandpa's portrait (ART4): 5 stages, 0 = full, 4 = last sliver. */
  timerRing: (stage: number) => `ui.timerRing.${stage}`,
  timerRingStages: 5,
  /** Particle sprites (ART4_vfx_particles). Every emitter draws from these; no procedural particle shapes. */
  vfx: {
    coin: 'vfx.coin', coinEdge: 'vfx.coinEdge', sparkle: 'vfx.sparkle', sparkleSmall: 'vfx.sparkleSmall',
    starWhite: 'vfx.starWhite', starPink: 'vfx.starPink', heart: 'vfx.heart', shard: 'vfx.shard', glow: 'vfx.glow',
    dirtBig: 'vfx.dirtBig', dirtSmall: 'vfx.dirtSmall', dustTan: 'vfx.dustTan', dustWhite: 'vfx.dustWhite',
    confetti: ['vfx.confetti.0', 'vfx.confetti.1', 'vfx.confetti.2'],
  },
  ui: { card: 'ui.card', woodSign: 'ui.woodSign', tinSign: 'ui.tinSign', cta: 'ui.cta', secondary: 'ui.secondary', iconRound: 'ui.iconRound', toggleOn: 'ui.toggle.on', toggleOff: 'ui.toggle.off', starEmpty: 'ui.star.empty', starFull: 'ui.star.full' },
} as const;

/**
 * Visible size of each sprite class as a fraction of a cell (PM review after WP4b M1). Sliced art
 * fills its texture box exactly (`fill: 1` in slices.json), so these are the only size knobs.
 */
export const SPRITE_SCALE = {
  pip: 0.85,
  sack: 0.9,
  creature: 0.8,
  gem: 0.6,
  bonus: 0.7,
  gold: 0.8,
} as const;

/** Build stamp injected by Vite (git short sha + ISO time); 'dev' when running under `vite dev`. */
declare const __BUILD__: string;
export const BUILD_STAMP: string = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev';
